import type { DealFormData, DealFormDataWithTargeting, TargetingSectionDealFormSwitch } from '@openx/types/deal.ts';
import type { OptionsMap, ThirdPartyPartnerOptionApi } from '@openx/types/options.ts';
import type { Package, PackageFormState } from '@openx/types/package.ts';
import { createContext, type PropsWithChildren, useContext } from 'react';

export type DealFormProviderProps = {
  pckg: Package | null;
  pckgUid: string | null;
  pckgIsLoading: boolean;
  /** Initial package is still loading after the form was rendered - its targeting is not in the form values yet */
  pckgIsInitialLoading?: boolean;
  /** Initial package fetch finished without returning the expected package */
  pckgInitialLoadFailed?: boolean;
  pckgFetch: (pckgId: string) => void;
  pckgClear: (isViaInteraction?: boolean) => void;
  pckgReset: () => void;
  pckgInitialValues: PackageFormState;
  pckgIsViaInteraction?: boolean;
  dealInitialValues: DealFormDataWithTargeting | DealFormData | null;
  pckgInitialMode?: TargetingSectionDealFormSwitch;
  targetablePckgs: OptionsMap;
  targetableCuratedPckgs: OptionsMap;
  targetablePckgsAreLoading: boolean;
  thirdPartyPartnerOptions: Record<string, ThirdPartyPartnerOptionApi>;
  thirdPartyPartnerOptionsFetch: (accountUid: string | null) => void;
  thirdPartyPartnerOptionsLoading: boolean;
};

export type DealFormContextProps = DealFormProviderProps;

const dealFormContext = createContext<DealFormContextProps>({
  pckg: null,
  pckgIsLoading: false,
  pckgIsInitialLoading: false,
  pckgInitialLoadFailed: false,
  pckgUid: null,
  pckgFetch: () => undefined,
  pckgClear: () => undefined,
  pckgReset: () => undefined,
  pckgInitialValues: {} as PackageFormState,
  pckgIsViaInteraction: false,
  dealInitialValues: {} as DealFormDataWithTargeting,
  pckgInitialMode: undefined,
  targetableCuratedPckgs: {},
  targetablePckgs: {},
  targetablePckgsAreLoading: false,
  thirdPartyPartnerOptions: {},
  thirdPartyPartnerOptionsFetch: () => undefined,
  thirdPartyPartnerOptionsLoading: false,
});

export const useDealFormContext = () => useContext(dealFormContext);

export function DealFormProvider({ children, ...props }: PropsWithChildren<DealFormProviderProps>) {
  return <dealFormContext.Provider value={{ ...props }}>{children}</dealFormContext.Provider>;
}
